/*
 * Unified file-picker accept lists (client review item 14).
 *
 * OS file dialogs (notably macOS Finder and Windows Explorer) grey out the
 * "Open" button when a file's MIME association is missing and the accept list
 * only contains MIME patterns. Listing explicit EXTENSIONS alongside the MIME
 * wildcard makes every dialog enable selection reliably — single and multi.
 * Every uploader in the app must use these constants (vet-doc pattern).
 */
export const PHOTO_EXTS = ["jpg", "jpeg", "png", "webp", "avif", "gif", "heic", "heif", "svg", "bmp"];
export const VIDEO_EXTS = ["mp4", "mov", "m4v", "webm", "avi", "mpg", "mpeg", "mkv"];

export const ACCEPT_PHOTOS = `${PHOTO_EXTS.map((e) => `.${e}`).join(",")},image/*`;
export const ACCEPT_VIDEOS = `${VIDEO_EXTS.map((e) => `.${e}`).join(",")},video/*`;
export const ACCEPT_MEDIA = `${ACCEPT_PHOTOS},${ACCEPT_VIDEOS}`;
export const ACCEPT_DOCS = `.pdf,application/pdf,${ACCEPT_PHOTOS}`;
export const ACCEPT_DOCS_AND_VIDEO = `${ACCEPT_DOCS},${ACCEPT_VIDEOS}`;

/** Classify a picked file even when the browser reports an empty MIME type. */
export const inferKind = (file, fallback = "document") => {
  if (file.type.startsWith("image/")) return "photo";
  if (file.type.startsWith("video/")) return "video";
  if (file.type === "application/pdf") return "document";
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (PHOTO_EXTS.includes(ext)) return "photo";
  if (VIDEO_EXTS.includes(ext)) return "video";
  return fallback;
};

/** Honest, specific upload errors — a generic toast hides root causes
 *  (client re-test: "Something went wrong" told them nothing). */
export const uploadErrorMessage = (err, t) => {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (status === 415) return t("common.uploadUnsupported", { supported: detail?.supported || "JPG, PNG, WebP, SVG" });
  if (status === 413) return t("common.uploadTooLarge", { max: detail?.max_mb || 50 });
  if (status === 507) return t("common.uploadNoSpace");
  return t("common.error");
};

/* ------------------------------------------------------------------ */
/* Direct Creator Uploads a Cloudflare Stream (P0 jul-2026).           */
/* Los bytes van DIRECTOS del navegador a Cloudflare vía TUS           */
/* (resumable): sin disco local en el backend, reintento tras cortes   */
/* de red y registro idempotente. Sustituye al pipeline de chunks.     */
/* ------------------------------------------------------------------ */
const TUS_CHUNK_SIZE = 8 * 1024 * 1024; // múltiplo de 256 KiB, >= mínimo TUS de Stream
const TUS_RETRY_DELAYS = [0, 1000, 3000, 5000, 10000, 20000];

/**
 * Sube un vídeo directo a Cloudflare Stream y registra el MediaAsset.
 * `session` ({uid, uploadUrl}) permite REANUDAR una subida interrumpida:
 * TUS consulta el offset ya recibido y continúa sin reenviar bytes.
 * Los errores de transporte llevan `uploadSession` para el botón Reintentar.
 */
export async function uploadVideoDirect(file, { category, onProgress, session } = {}) {
  const { api } = await import("@/lib/api");
  let s = session;
  if (!s) {
    const { data } = await api.post("/media/videos/direct-upload", {
      filename: file.name,
      size: file.size,
      content_type: file.type || "",
      category: category || null,
    });
    s = { uid: data.uid, uploadUrl: data.upload_url };
  }
  const tus = await import("tus-js-client");
  await new Promise((resolve, reject) => {
    const upload = new tus.Upload(file, {
      uploadUrl: s.uploadUrl,
      chunkSize: TUS_CHUNK_SIZE,
      retryDelays: TUS_RETRY_DELAYS,
      metadata: { filename: file.name, filetype: file.type || "video/mp4" },
      onProgress: (sent, total) => onProgress?.(Math.min(95, Math.round((sent / total) * 100))),
      onError: (err) => {
        const e = err instanceof Error ? err : new Error("upload_failed");
        e.uploadSession = s;
        e.isTransportError = true;
        reject(e);
      },
      onSuccess: resolve,
    });
    upload.start();
  });
  try {
    const { data: asset } = await api.post(`/media/videos/direct-upload/${s.uid}/finish`, {
      category: category || null,
    });
    onProgress?.(100);
    return asset;
  } catch (err) {
    // el finish reintentado es idempotente: conserva la sesión para Reintentar
    if (!err.response || err.response.status >= 500) {
      err.uploadSession = s;
      err.isTransportError = true;
    }
    throw err;
  }
}

/** Descarte explícito de una subida fallida no registrada (best effort). */
export async function discardVideoDirectUpload(uid) {
  const { api } = await import("@/lib/api");
  await api.delete(`/media/videos/direct-upload/${uid}`).catch(() => {});
}

/** Poster/thumbnail for an organizer-side MediaAsset video. */
export const videoPosterSrc = (asset, backendUrl) => {
  if (!asset) return null;
  if (asset.poster_path) return `${backendUrl}/api/media/poster/${asset.id}`;
  return asset.poster_url || null;
};
